import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGridPremium,
  GridApiPremium,
  GridColDef,
  GridComparatorFn,
  gridFilteredRowCountSelector,
  GridFilterOperator,
  gridNumberComparator,
} from '@mui/x-data-grid-premium';
import { StackedTextCell } from '@retail/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import { ItemPriority } from '@retail/products/types';
import { MutableRefObject, useEffect, useMemo } from 'react';
import { Close, Straight } from '@mui/icons-material';
import { DateSelectField } from '../DateSchema/DateSelectField';
import { EditablePriorityCell } from './EditablePriorityCell';
import { DateSelectMenu } from '../DateSchema';
import { SwapPriorityMenu } from './SwapPriorityMenu';
import { useFilters, useItemEditActions } from '@retail/products/hooks';
import { TrashIcon, WarningIcon } from '@shared/custom-icons';
import { StockedStatusMenu } from './StockedStatusMenu';
import { BulkActionsFilters, itemBulkActions } from '@retail/products/utils';

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  apiRef: MutableRefObject<GridApiPremium | null>;
  assortmentItems: AssortmentItemMinimal[];
  includeStocked: boolean;
  duplicateRowIds: number[];
  setRowCount: (count: number) => void;
  setDuplicateRowIds: (ids: number[]) => void;
  removeItem: (mgSupplierItemId: number) => void;
}

const productGroupOperator: GridFilterOperator<any, number[]>[] = [
  {
    label: 'IncludesProductGroup',
    value: 'includesProductGroup',
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value.length) return null;
      return (groupNumbers) => {
        const includes = groupNumbers.filter((groupNumber) =>
          filterItem.value.includes(groupNumber + '')
        );
        return includes.length > 0;
      };
    },
  },
];

const itemSearchOperator: GridFilterOperator<any, { name: string; id: number }>[] = [
  {
    label: 'ItemSearch',
    value: 'containsItemSearch',
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value) return null;
      return (mgItem) => {
        return (
          mgItem.name.toLowerCase().includes(filterItem.value) ||
          (mgItem.id + '').includes(filterItem.value)
        );
      };
    },
  },
];

const itemSearchSortComparator: GridComparatorFn = (v1, v2, param1, param2) => {
  return gridNumberComparator(v1.id, v2.id, param1, param2);
};

export function SelectedItemsTable({
  apiRef,
  assortmentItems,
  includeStocked,
  duplicateRowIds,
  setRowCount,
  setDuplicateRowIds,
  removeItem,
}: Props) {
  const t = useMyAssortmentTFunction();

  const { filters } = useFilters<BulkActionsFilters>(itemBulkActions);
  const {
    handleDateChange,
    handlePriorityChange,
    handleStockedChange,
    handleBulkChangeDates,
    handleBulkChangePriority,
    handleBulkChangeStockedStatus,
  } = useItemEditActions({ apiRef, assortmentItems, duplicateRowIds, setDuplicateRowIds });

  const columns: GridColDef<AssortmentItemMinimal>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        width: 300,
        field: 'mgItem',
        sortable: true,
        headerName: t('myAssortment.columns.itemText'),
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={<Typography fontWeight="600">{row.primaryText}</Typography>}
            bottomLine={row.mgItemNumber}
          />
        ),
        valueGetter: (_, row) => {
          return { name: row.primaryText, id: row.mgItemNumber };
        },
        filterOperators: itemSearchOperator,
        sortComparator: itemSearchSortComparator,
      },
      {
        ...baseColumnProps,
        width: 220,
        field: 'supplier',
        sortable: true,
        headerName: t('myAssortment.columns.supplier'),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.supplier.name} bottomLine={row.supplier.id} />
        ),
        valueGetter: (_, row) => row.supplier.id,
      },
      {
        field: 'discountGroup',
        valueGetter: (_, row) => row.discountGroup?.id,
      },
      {
        field: 'productGroup',
        valueGetter: (_, row) => [
          row.productGroup.groupNumber.slice(0, 2),
          row.productGroup.groupNumber.slice(0, 3),
          row.productGroup.groupNumber,
        ],
        filterOperators: productGroupOperator,
      },
      {
        ...baseColumnProps,
        width: 170,
        field: 'validFrom',
        headerName: t('myAssortment.columns.validFrom'),
        renderHeader: () => (
          <Stack flexDirection="row" alignItems="center" gap={4.5}>
            <Typography variant="subtitle1">{t('myAssortment.columns.validFrom')}</Typography>
            <DateSelectMenu
              handleChangeDate={(newDate: Date | null) =>
                handleBulkChangeDates('validFrom', newDate)
              }
            />
          </Stack>
        ),
        renderCell: ({ row }) => (
          <DateSelectField
            value={row.validFrom ?? new Date()}
            maxDate={row.validTo}
            setNewDate={(newDate: Date | null) =>
              handleDateChange('validFrom', row.mgSupplierItemId, newDate)
            }
          />
        ),
      },
      {
        ...baseColumnProps,
        width: 200,
        field: 'validTo',
        headerName: t('myAssortment.columns.validTo'),
        renderHeader: () => (
          <Stack flexDirection="row" alignItems="center" gap={7.5}>
            <Typography variant="subtitle1">{t('myAssortment.columns.validTo')}</Typography>
            <Stack flexDirection="row" alignItems="center">
              <IconButton sx={{ p: 0 }} onClick={() => handleBulkChangeDates('validTo', null)}>
                <Close fontSize="small" />
              </IconButton>
              <DateSelectMenu
                handleChangeDate={(newDate: Date | null) =>
                  handleBulkChangeDates('validTo', newDate)
                }
              />
            </Stack>
          </Stack>
        ),
        renderCell: ({ row }) => (
          <DateSelectField
            clearable
            minDate={row.validFrom}
            value={row.validTo}
            setNewDate={(newDate: Date | null) =>
              handleDateChange('validTo', row.mgSupplierItemId, newDate)
            }
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'priority',
        width: 160,
        headerName: t('myAssortment.priority.priority'),
        renderHeader: () => (
          <Stack flexDirection="row" alignItems="center" gap={1.5}>
            <Typography variant="subtitle1">{t('myAssortment.priority.priority')}</Typography>
            <SwapPriorityMenu handleUpdatePriority={handleBulkChangePriority} />
          </Stack>
        ),
        renderCell: ({ row }) => (
          <Stack flexDirection="row" alignItems="center">
            <EditablePriorityCell
              priority={row.priority?.prioritised ? ItemPriority.TEN : ItemPriority.ZERO}
              handlePriorityChange={() => handlePriorityChange(row.mgSupplierItemId)}
            />
            {!!duplicateRowIds.includes(row.mgItemNumber) && (
              <Tooltip title={t('myAssortment.priority.duplicatePriority')}>
                <Stack>
                  <WarningIcon color="error" />
                </Stack>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        ...baseColumnProps,
        field: 'stockedStatus',
        headerName: t('myAssortment.stockStatus.status'),
        renderHeader: () => (
          <Stack flexDirection="row" alignItems="center" gap={0.5}>
            <Typography variant="subtitle2">{t('myAssortment.stockStatus.status')}</Typography>
            <StockedStatusMenu handleUpdateStockedStatus={handleBulkChangeStockedStatus} />
          </Stack>
        ),
        renderCell: ({ row }) => (
          <Stack flexDirection="row" gap={0.3}>
            <Typography variant="subtitle2">
              {row.isStocked === false
                ? t('myAssortment.stockStatus.order')
                : t('myAssortment.stockStatus.inStock')}
            </Typography>
            <IconButton
              sx={{ p: 0 }}
              onClick={() => handleStockedChange(row.mgSupplierItemId, row.isStocked ?? true)}
            >
              {row.isStocked === false ? (
                <Straight fontSize="small" />
              ) : (
                <Straight sx={{ rotate: '180deg' }} fontSize="small" />
              )}
            </IconButton>
          </Stack>
        ),
      },
      {
        ...baseColumnProps,
        field: 'actions',
        align: 'center',
        width: 50,
        headerName: '',
        renderCell: ({ row }) => (
          <IconButton onClick={() => removeItem(row.mgSupplierItemId)}>
            <TrashIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [
      duplicateRowIds,
      handleBulkChangeDates,
      handleBulkChangePriority,
      handleBulkChangeStockedStatus,
      handleDateChange,
      handlePriorityChange,
      handleStockedChange,
      removeItem,
      t,
    ]
  );

  useEffect(() => {
    apiRef.current?.setFilterModel({
      items: [
        { id: 'supplier', field: 'supplier', operator: 'isAnyOf', value: filters.supplierModal },
        {
          id: 'discountGroup',
          field: 'discountGroup',
          operator: 'isAnyOf',
          value: filters.discountGroupModal,
        },
        {
          id: 'productGroup',
          field: 'productGroup',
          operator: 'includesProductGroup',
          value: filters.productGroupModal,
        },
        {
          id: 'mgItem',
          field: 'mgItem',
          operator: 'containsItemSearch',
          value: filters.searchQueryModal,
        },
      ],
    });
    setRowCount(gridFilteredRowCountSelector(apiRef));
  }, [
    apiRef,
    assortmentItems.length,
    setRowCount,
    filters.supplierModal,
    filters.discountGroupModal,
    filters.productGroupModal,
    filters.searchQueryModal,
  ]);

  return (
    <Stack flexGrow={1}>
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <DataGridPremium<AssortmentItemMinimal>
            apiRef={apiRef}
            rows={assortmentItems}
            columns={columns}
            initialState={{
              pinnedColumns: { right: ['actions'] },
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={{
              discountGroup: false,
              productGroup: false,
              stockedStatus: includeStocked,
            }}
            disableColumnReorder
            hideFooter
            rowHeight={65}
            getRowId={(row) => row.mgSupplierItemId}
            disableColumnFilter
            disableColumnMenu
          />
        </Box>
      </Box>
    </Stack>
  );
}
